import { FC, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Button, Dialog } from '@mui/material';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { PaginateList } from 'src/app/common/types';
import { CampaignCategoryItem, CampaignCategoryUpdateItem } from '../../../../types/campaign-category';
import PruTable from 'src/app/common/components/PruTable/PruTable';
import { getDefaultDisplayDate, useJWT, isPermitted } from 'src/app/common/utils';
import { CampaignCategoryPermissionType } from 'src/app/modules/Config/constants';
import { configCampaignCategoryPath } from '../../ConfigCampaignCategoryPage';
import { useDispatch } from 'react-redux';
import { map } from 'lodash';
import ItemDeleteDialog from 'src/app/common/components/ItemDeleteDialog';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { deleteCampaignCategory, modifyCampaignCategory } from 'src/app/modules/Config/network/campaignCategoryCrud';

type CampaignCategoryProps = {
  isLoading: boolean;
  campaignCategoryList?: PaginateList<CampaignCategoryItem>;
  onRefresh: () => void;
  onChangePage: (page: number, rowsPerPage: number) => void;
};
type DialogState = {
  open: boolean;
  id: string;
};

const useStyles = makeStyles()((theme) => ({
  dialogContainer: {
    padding: '20px',
  },
  btnContainer: {
    padding: '0 20px 20px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorContainer: {
    padding: 20,
    marginBottom: 20,
    borderRadius: 5,
    backgroundColor: theme.palette.common.white,
  },
  errorHeader: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    marginBottom: 10,
  },
  errorFooter: {
    marginTop: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const CampaignCategoryList: FC<CampaignCategoryProps> = ({
  isLoading,
  campaignCategoryList,
  onRefresh,
  onChangePage,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const { classes } = useStyles();
  const Translation = (id: string) => intl.formatMessage({ id });

  let updatePermission = false;
  let createPermission = false;
  const jwt = useJWT() || {};

  if (jwt.permissions) {
    const jwtPermission = map(jwt.permissions, (_, key) => key);
    updatePermission = isPermitted([CampaignCategoryPermissionType.CONFIG_CAMPAIGN_CATEGORY_UPDATE], jwtPermission);
    createPermission = isPermitted([CampaignCategoryPermissionType.CONFIG_CAMPAIGN_CATEGORY_CREATE], jwtPermission);
  }

  const [errorDialog, setErrorDialog] = useState({
    show: false,
    message: '',
  });

  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    id: '',
  });

  const onPause = async (id: string) => {
    try {
      const details: CampaignCategoryUpdateItem = {
        active: false,
      };

      await modifyCampaignCategory(id, details, dispatch);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: `Campaign Category paused successfully`,
          },
        ]),
      );
      onRefresh();
    } catch (err) {}
  };

  const onDelete = async (id: string) => {
    try {
      await deleteCampaignCategory(id, dispatch);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: `Campaign Category deleted successfully`,
          },
        ]),
      );
    } catch (err) {}
  };

  return (
    <>
      <ItemDeleteDialog
        key={`delete-campaign-dialog-${dialogState.open}`}
        open={dialogState.open}
        onClose={() => setDialogState({ open: false, id: '' })}
        onRefresh={onRefresh}
        onDelete={() => onDelete(dialogState.id)}
      />
      <PruTable
        title={Translation('configCampaignCategory.list')}
        disableBulkSelect
        headerBtnDef={[
          {
            color: 'primary',
            title: Translation('configCampaignCategory.newCategory'),
            onClick: () => {
              history.push(`${configCampaignCategoryPath}/create`);
            },
            condition: () => createPermission,
          },
        ]}
        operationDef={[
          {
            title: Translation('section.common.operation.edit'),
            tooltipText: 'Edit Campaign Category Detail',
            onClick: (row) => {
              history.push(`${configCampaignCategoryPath}/edit/${row._id}`);
            },
            condition: () => updatePermission,
          },
          {
            title: Translation('configCampaignCategory.pause'),
            tooltipText: 'Pause Campaign Category',
            condition: (row) => updatePermission && row.active,
            onClick: (row) => onPause(row._id),
          },
          {
            title: Translation('section.common.operation.delete'),
            tooltipText: 'Delete Campaign Category',
            condition: () => updatePermission,
            onClick: (row) => setDialogState({ open: true, id: row._id }),
          },
        ]}
        columnDef={[
          {
            keyIndex: 'category',
            align: 'center',
            displayName: 'ID',
            hidden: true,
            renderData: (row) => (row._id ? row._id : ''),
          },
          {
            keyIndex: 'namezh',
            align: 'center',
            displayName: Translation(`configCampaignCategory.common.categoryNameHK`),
            renderData: (row) => (row.i18nData ? row.i18nData['zh-HK']?.name : '-') || '-',
          },
          {
            keyIndex: 'nameen',
            align: 'center',
            displayName: Translation(`configCampaignCategory.common.categoryNameEn`),
            renderData: (row) => (row.i18nData ? row.i18nData['en']?.name : '-') || '-',
          },
          {
            keyIndex: 'updatedBy',
            align: 'center',
            displayName: Translation('configCampaignCategory.common.updatedBy'),
            renderData: (row) => (row.updatedBy ? row.updatedBy : ''),
          },
          {
            keyIndex: 'createdAt',
            align: 'center',
            displayName: Translation('configCampaignCategory.common.createdAt'),
            renderData: (row) => getDefaultDisplayDate(row.createdAt, 'datetime'),
          },
          {
            keyIndex: 'updatedAt',
            align: 'center',
            displayName: Translation('configCampaignCategory.common.updatedAt'),
            renderData: (row) => getDefaultDisplayDate(row.updatedAt, 'datetime'),
          },
          {
            keyIndex: 'order',
            align: 'center',
            displayName: Translation('configCampaignCategory.common.order'),
            renderData: (row) => String(row?.order),
          },
          {
            keyIndex: 'isDefault',
            align: 'center',
            displayName: Translation('configCampaignCategory.common.isDefault'),
            renderData: (row) =>
              row.isDefault
                ? Translation('configCampaignCategory.common.isDefault.true')
                : Translation('configCampaignCategory.common.isDefault.false'),
          },
          {
            keyIndex: 'active',
            align: 'center',
            displayName: Translation('configCampaignCategory.common.status'),
            renderData: (row) =>
              row.active
                ? Translation('configCampaignCategory.common.status.active')
                : Translation('configCampaignCategory.common.status.pause'),
          },
        ]}
        isLoading={isLoading}
        onRefresh={onRefresh}
        dataSource={campaignCategoryList?.docs}
        totalPages={campaignCategoryList?.totalPages}
        totalRecords={campaignCategoryList?.totalDocs}
        onChangePage={onChangePage}
      />

      {/** Error Dialog */}
      <Dialog
        onClose={() => {
          setErrorDialog((prev) => ({
            ...prev,
            show: false,
          }));
        }}
        open={errorDialog.show}
      >
        <div className={classes.errorContainer}>
          <div>{errorDialog.message}</div>
          <div className={classes.errorFooter}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setErrorDialog((prev) => ({
                  ...prev,
                  show: false,
                }));
              }}
            >
              OK
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default CampaignCategoryList;
